import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Info } from 'lucide-react';

export const HowToReadResults = ({ mode }) => {
  const isSeeking = mode === 'seeking';

  const blocks = isSeeking
    ? [
        {
          title: 'Importance defines what matters',
          text: 'Each driver is scored on how much it matters to you. The drivers you rated highest are the ingredients that define what this experience needs to feel right.'
        },
        {
          title: 'Experience confidence reflects clarity',
          text: 'Your confidence level tells you whether this is something you know works or something you are still exploring. Both are useful — they calibrate how to read everything else.'
        },
        {
          title: 'This is a map, not a diagnosis',
          text: 'These results define what you are looking for, not what you are missing. Use them to understand what makes this kind of experience feel right for you.'
        },
        {
          title: 'Different contexts can differ',
          text: 'If you created multiple profiles, they may show different patterns. That is normal. What works in one context may not apply in another.'
        }
      ]
    : [
        {
          title: 'What matters most defines the profile',
          text: 'Each driver is scored on how much it matters to you. The drivers you rated highest are the ingredients that define what this experience needs to feel right.'
        },
        {
          title: 'When present vs when not',
          text: 'The report shows what happens when key ingredients are present, and what happens when they are not. This contrast is where the most useful insight lives.'
        },
        {
          title: 'Different contexts can differ',
          text: 'If you created multiple profiles, they may show different patterns. That is normal. What works in one context may not apply in another.'
        },
        {
          title: 'This measures experience, not frequency',
          text: 'These results reflect the quality and shape of your experience, not how often things happen. When the experience does not land, it usually means the right ingredients are not in place — not that you need more of everything.'
        }
      ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card className="border-border/60 bg-card/80 mb-10" data-testid="how-to-read-results">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-lg font-light text-foreground">How to Read Your Results</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {blocks.map((block, i) => (
            <div key={i} className="flex gap-3">
              <span className="text-xs text-muted-foreground/60 font-medium mt-0.5 shrink-0">{i + 1}.</span>
              <div>
                <span className="text-sm font-medium text-foreground">{block.title}: </span>
                <span className="text-sm text-muted-foreground leading-relaxed">{block.text}</span>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </motion.div>
  );
};
